import React, { useEffect, useMemo } from 'react';
import type { SetNonNullable } from 'type-fest';
import type {
  MagneticWallFocus,
  MagneticWallHighlight,
} from '../../editor/editorSlice';
import VersionChange from '../../editor/formats/VersionChange';
import { useQuill } from '../../editor/QuillContext';
import type { DocumentType } from '../../graphql/generated';
import { gql } from '../../graphql/generated';
import { isCommentOrPublicCommentThread } from '../../utils/post/threadUtils';
import type { ElementWithIds } from '../MagneticWallLayout';
import MagneticWallLayout, {
  getContentRectsMap,
  useMagneticWallLayout,
} from '../MagneticWallLayout';
import useVisibleMagneticWallItems from '../useVisibleMagneticWallItems';
import type { AnnotationVersionChange } from './AnnotationVersionChangeCard';
import AnnotationVersionChangeCard from './AnnotationVersionChangeCard';
import type { EditVersionChange } from './EditVersionChangeCard';
import EditVersionChangeCard from './EditVersionChangeCard';
import type { ThreadVersionChange } from './ThreadVersionChangeCard';
import ThreadVersionChangeCard from './ThreadVersionChangeCard';
import { useVersionChangesContext } from './VersionChanges';
import { queryMagneticWallElementsWithIds } from '../MagneticWall';

const HISTORY_FRAGMENT = gql(`
  fragment NewVersionChangesWall_history on History {
    id
    threads {
      id
      ...ThreadVersionChangeCard_thread
    }
    annotations {
      id
      ...AnnotationVersionChangeCard_annotation
    }
  }
`);

const POST_FRAGMENT = gql(`
  fragment NewVersionChangesWall_post on Post {
    id
    ...ThreadVersionChangeCard_post
    ...AnnotationVersionChangeCard_post
  }
`);

type NewVersionChange =
  | EditVersionChange
  | ThreadVersionChange
  | AnnotationVersionChange;

interface InnerNewVersionChangesWallProps {
  isAtCurrentHistory: (version: number) => boolean;
  versionChanges: NewVersionChange[];
  post: DocumentType<typeof POST_FRAGMENT>;
  goToVersion: (version: number) => void;
  setMagneticWallFocus: (magneticWallFocus: MagneticWallFocus | null) => void;
  magneticWallFocus: MagneticWallFocus | null;
  setMagneticWallHighlight: (
    magneticWallHighlight: MagneticWallHighlight | null,
  ) => void;
  historyInsertedAt: Date;
  versionFrom: number;
  versionTo: number;
}

const InnerNewVersionChangesWall = ({
  isAtCurrentHistory,
  versionChanges,
  historyInsertedAt,
  post,
  goToVersion,
  setMagneticWallFocus,
  magneticWallFocus,
  setMagneticWallHighlight,
  versionFrom,
  versionTo,
}: InnerNewVersionChangesWallProps) => {
  const { layout, debouncedUpdateDesiredPositions, updateThreadHeights } =
    useMagneticWallLayout();
  const { focusedId, focusById } = useVersionChangesContext();

  const visibleItems = useMemo(
    () =>
      versionChanges
        .map((item) => ({
          ...item,
          top: layout.tops[item.id],
          isOverlapped: layout.overlappedIds.has(item.id),
        }))
        .filter(
          (item): item is SetNonNullable<typeof item, 'top'> =>
            item.top != null,
        ),
    [layout.overlappedIds, layout.tops, versionChanges],
  );

  useEffect(() => {
    debouncedUpdateDesiredPositions();
    updateThreadHeights();
  }, [visibleItems, debouncedUpdateDesiredPositions, updateThreadHeights]);

  return (
    <>
      {visibleItems.map((versionChange) => {
        if (versionChange.type === 'thread') {
          const isFocused = versionChange.id === magneticWallFocus?.id;
          return (
            <MagneticWallLayout.Item
              id={versionChange.id}
              focused={isFocused}
              top={versionChange.top}
              key={versionChange.id}
            >
              <ThreadVersionChangeCard
                post={post}
                focused={isFocused}
                versionChange={versionChange}
                historyInsertedAt={historyInsertedAt}
                versionFrom={versionFrom}
                versionTo={versionTo}
                onFocus={() =>
                  setMagneticWallFocus({
                    id: versionChange.id,
                    type: 'comment',
                    active: true,
                    hasExpandIntent: false,
                  })
                }
                onRemoveHighlight={() => setMagneticWallHighlight(null)}
                onHighlight={() =>
                  setMagneticWallHighlight({
                    id: versionChange.id,
                    type: 'comment',
                  })
                }
              />
            </MagneticWallLayout.Item>
          );
        }

        if (versionChange.type === 'annotation') {
          const isFocused = versionChange.id === magneticWallFocus?.id;

          return (
            <MagneticWallLayout.Item
              id={versionChange.id}
              focused={isFocused}
              top={versionChange.top}
              key={versionChange.id}
            >
              <AnnotationVersionChangeCard
                post={post}
                focused={isFocused}
                versionChange={versionChange}
                versionFrom={versionFrom}
                versionTo={versionTo}
                onFocus={() =>
                  setMagneticWallFocus({
                    id: versionChange.id,
                    type: 'annotation',
                    active: true,
                    hasExpandIntent: false,
                  })
                }
                onRemoveHighlight={() => setMagneticWallHighlight(null)}
                onHighlight={() =>
                  setMagneticWallHighlight({
                    id: versionChange.id,
                    type: 'annotation',
                  })
                }
              />
            </MagneticWallLayout.Item>
          );
        }

        const isFocused =
          magneticWallFocus == null && versionChange.id === focusedId;

        return (
          <MagneticWallLayout.Item
            id={versionChange.id}
            focused={isFocused}
            top={versionChange.top}
            key={versionChange.id}
          >
            <EditVersionChangeCard
              isAtCurrentHistory={isAtCurrentHistory}
              versionChange={versionChange}
              focused={isFocused}
              onClick={() => {
                focusById(versionChange.id);
              }}
              historyInsertedAt={historyInsertedAt}
              goToVersion={() => {
                goToVersion(versionChange.version);
              }}
            />
          </MagneticWallLayout.Item>
        );
      })}
    </>
  );
};

function queryVersionChangeElementsWithIds(
  container: HTMLElement,
): ElementWithIds[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(`[${VersionChange.keyName}]`),
  ).map((element) => ({
    element,
    ids: [VersionChange.value(element) as string],
  }));
}

interface NewVersionChangesWallProps {
  history: DocumentType<typeof HISTORY_FRAGMENT>;
  post: DocumentType<typeof POST_FRAGMENT>;
  isAtCurrentHistory: (version: number) => boolean;
  goToVersion: (version: number) => void;
  setMagneticWallFocus: (magneticWallFocus: MagneticWallFocus | null) => void;
  magneticWallFocus: MagneticWallFocus | null;
  setMagneticWallHighlight: (
    magneticWallHighlight: MagneticWallHighlight | null,
  ) => void;
  magneticWallHighlight: MagneticWallHighlight | null;
  historyInsertedAt: Date;
  versionFrom: number;
  versionTo: number;
}

const NewVersionChangesWall = ({
  history,
  post,
  isAtCurrentHistory,
  goToVersion,
  setMagneticWallFocus,
  magneticWallFocus,
  setMagneticWallHighlight,
  magneticWallHighlight,
  historyInsertedAt,
  versionFrom,
  versionTo,
}: NewVersionChangesWallProps) => {
  const { versionChanges: editVersionChanges } = useVersionChangesContext();

  const { annotations } = history;
  const threads = useMemo(
    () => history.threads.filter(isCommentOrPublicCommentThread),
    [history],
  );

  useVisibleMagneticWallItems([
    ...annotations.map((annotation) => ({
      id: annotation.id,
      type: 'annotation' as const,
    })),
    ...threads
      .filter((thread) => {
        return (
          thread.resolvedAt == null ||
          // If the thread is resolved, we only show focus & highlight
          magneticWallFocus?.id === thread.id ||
          magneticWallHighlight?.id === thread.id
        );
      })
      .map((thread) => ({ id: thread.id, type: 'comment' as const })),
  ]);

  const versionChanges: NewVersionChange[] = useMemo(
    () => [
      ...threads.map((thread) => ({
        id: thread.id,
        type: 'thread' as const,
        thread,
      })),
      ...annotations.map((annotation) => ({
        id: annotation.id,
        type: 'annotation' as const,
        annotation,
      })),
      ...editVersionChanges.map((change) => ({
        ...change,
        type: 'edit' as const,
      })),
    ],
    [editVersionChanges, threads, annotations],
  );

  const { quill } = useQuill();

  return (
    <MagneticWallLayout.Root
      magneticWallItems={versionChanges}
      magneticWallFocus={magneticWallFocus}
      getContentRects={() => {
        if (!quill) return {};
        const { container } = quill;
        return getContentRectsMap(
          [
            ...queryMagneticWallElementsWithIds(container),
            ...queryVersionChangeElementsWithIds(container),
          ],
          container,
        );
      }}
    >
      <InnerNewVersionChangesWall
        post={post}
        isAtCurrentHistory={isAtCurrentHistory}
        versionChanges={versionChanges}
        goToVersion={goToVersion}
        setMagneticWallFocus={setMagneticWallFocus}
        magneticWallFocus={magneticWallFocus}
        setMagneticWallHighlight={setMagneticWallHighlight}
        historyInsertedAt={historyInsertedAt}
        versionFrom={versionFrom}
        versionTo={versionTo}
      />
    </MagneticWallLayout.Root>
  );
};

export default NewVersionChangesWall;
